import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronDown, KeyRound, LogOut } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { confirmAbandonReportGeneration } from "../../utils/reportGenerationGuard";

const ProfileInfo = () => {
  const [showProfileWindow, setProfileWindow] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projectState, setProjectState] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const toggleProfileWindow = () => setProfileWindow((v) => !v);

  useEffect(() => {
    if (!showProfileWindow) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setProfileWindow(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showProfileWindow]);

  const handleLogout = async () => {
    const shouldLeave = await confirmAbandonReportGeneration("log out");
    if (!shouldLeave) {
      setProfileWindow(false);
      return;
    }
    try {
      setProfileWindow(false);
      await logout();
      window.location.replace("/signin");
    } catch (error) {
      console.error("Logout error:", error);
      window.location.replace("/signin");
    }
  };

  const changePassword = async () => {
    const shouldLeave = await confirmAbandonReportGeneration("leave this page");
    if (!shouldLeave) {
      setProfileWindow(false);
      return;
    }
    navigate("/change-password");
  };

  useEffect(() => {
    const fetchProjectState = async () => {
      try {
        const fetchedProjectState = await getProjectState();
        if (fetchedProjectState !== null) setProjectState(fetchedProjectState);
        else setProjectState(0);
      } catch (error) {
        console.error("Error fetching project state:", error);
      }
    };
    fetchProjectState();
  }, []);

  const showProjectStateNotification = (nextState: number) => {
    if (nextState === 0) {
      toast.info(
        <div>
          <strong>No report has been submitted</strong>
          <p>Begin by selecting your capabilities to tailor your experience.</p>
          <Link to="/capabilities" style={{ color: "#0F6B66", fontWeight: 500 }}>
            Capabilities
          </Link>
        </div>
      );
    } else if (nextState === 1) {
      toast.info(
        <div>
          <strong>Capabilities Incomplete</strong>
          <p>Continue selecting your capabilities to proceed to the assessment.</p>
          <Link to="/capabilities" style={{ color: "#0F6B66", fontWeight: 500 }}>
            Capabilities
          </Link>
        </div>
      );
    } else if (nextState === 2) {
      toast.info(
        <div>
          <strong>Continue Where you left off</strong>
          <p>
            You've selected your capabilities - now let's head to Assessment to wrap up your
            progress.
          </p>
          <Link to="/assessment" style={{ color: "#0F6B66", fontWeight: 500 }}>
            Continue to Assessment
          </Link>
        </div>
      );
    } else if (nextState === 3) {
      toast.success(
        <div>
          <strong>Report Submitted</strong>
          <p>
            Your assessment is under expert review. Once complete, you will be notified.
          </p>
        </div>
      );
    } else if (nextState === 4) {
      toast.success(
        <div>
          <strong>Your report is ready</strong>
          <p>Your assessment has been evaluated, and a report is created for you.</p>
        </div>
      );
    }
  };

  const getProjectState = () =>
    new Promise<number>((resolve) => {
      setTimeout(() => resolve(Math.floor(Math.random() * 5)), 1000);
    });

  // Keep unused helpers referenced so behaviour parity remains if wired later
  void projectState;
  void showProjectStateNotification;

  return (
    <div ref={rootRef} className="pi">
      <button
        type="button"
        onClick={toggleProfileWindow}
        className="pi__trigger"
        aria-expanded={showProfileWindow}
        aria-haspopup="menu"
      >
        <span className="pi__avatar">{(user?.userName || "U").charAt(0).toUpperCase()}</span>
        <span className="pi__meta">
          <span className="pi__name" style={{ display: "block" }}>
            {user == null ? "—" : user.userName}
          </span>
          <span className="pi__role" style={{ display: "block" }}>
            {user == null ? "—" : user.jobTitle}
          </span>
        </span>
        <ChevronDown className="pi__chevron" size={16} />
      </button>

      {showProfileWindow ? (
        <div role="menu" className="pi__menu">
          <button type="button" role="menuitem" className="pi__menu-item" onClick={changePassword}>
            <KeyRound size={16} />
            Reset Password
          </button>
          <button type="button" role="menuitem" className="pi__menu-item" onClick={handleLogout}>
            <LogOut size={16} />
            Logout
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ProfileInfo;
